import React, { useCallback, useRef, useState } from 'react';
import { Box, Button, LinearProgress, Typography, Paper } from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { DirCategory } from '../services/myDriveService';
import myDriveService from '../services/myDriveService';
import { useMessage } from '../../../../contexts/MessageContext';
import { useTranslation } from 'react-i18next';

interface Props {
  dir_category: DirCategory;
  prefix: string;
  onUploaded: () => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;  // 外部傳入 ref（MyDrivePage 用）
}

export default function FileUploader({ dir_category, prefix, onUploaded, disabled, inputRef: externalRef }: Props) {
  const { showSuccess, showApiError } = useMessage();
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef ?? internalRef;

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    setProgress(0);
    const total = files.length;
    let done = 0;
    for (const f of Array.from(files)) {
      const rel = prefix ? `${prefix}${f.name}` : f.name;
      try {
        await myDriveService.uploadFile(dir_category, rel, f);
        done++;
        setProgress(Math.round((done / total) * 100));
      } catch (err) {
        showApiError(err);
      }
    }
    setUploading(false);
    if (done > 0) {
      showSuccess('SYS330001', { path: prefix || '/' });
      onUploaded();
    }
  }, [dir_category, prefix, onUploaded, showSuccess, showApiError]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, textAlign: 'center', opacity: disabled ? 0.5 : 1 }}
      onDrop={disabled ? undefined : onDrop}
      onDragOver={e => e.preventDefault()}
    >
      <UploadIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('myDrive.dragDrop', '拖拉檔案到此處，或')}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
      >
        {t('myDrive.selectFiles', '選擇檔案')}
      </Button>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={e => handleFiles(e.target.files)}
      />
      {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
    </Paper>
  );
}
